import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const HEADER_PREFIX_SPECIALS = /[.*+?^${}()|[\]\\]/g

export function buildReportFilename(prefix, startDate, endDate) {
  const start = startDate || 'start'
  const end = endDate || 'end'
  return `${prefix}_${start}_to_${end}.xlsx`
}

export function downloadBase64Excel(base64, filename) {
  if (!base64) return false

  const safeName = filename || 'report.xlsx'
  const link = document.createElement('a')
  link.href = `data:${XLSX_MIME};base64,${base64}`
  link.download = safeName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  return true
}

export function stripHtmlForExport(value) {
  if (value == null || value === '') return ''
  return String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n|\r/g, '\n')
    .trim()
}

export function cleanExportText(value, headerLabel) {
  let text = stripHtmlForExport(value)
  if (!text) return ''
  text = text.split('\n')[0].trim()
  if (headerLabel) {
    const escaped = String(headerLabel).replace(HEADER_PREFIX_SPECIALS, '\\$&')
    text = text.replace(new RegExp(`^${escaped}\\s*[:#\\u2013-]\\s*`, 'i'), '').trim()
  }
  const columnPrefix = /^column(?:\s*\d+)?\s*[:#\u2013-]?\s*/i
  while (columnPrefix.test(text)) {
    text = text.replace(columnPrefix, '').trim()
  }
  if (/^column$/i.test(text)) return ''
  return text
}

export function cleanExportJobId(value) {
  let text = cleanExportText(value, 'Job ID')
  text = text.replace(/^job\s*id\s*[:#\u2013-]?\s*/i, '').trim()
  text = text.replace(/^job\s*#\s*/i, '').trim()
  return text
}

export function cleanExportDaysOpen(value) {
  if (value == null || value === '') return ''
  const text = String(value).replace(/days?\s*open/i, '').replace(/,/g, '').trim()
  const n = Number(text)
  return Number.isFinite(n) ? n : ''
}

export function mapTableRowsForExport(rows, fields) {
  const exportFields = (fields || []).filter(field => field && field.key)
  if (!exportFields.length) return rows
  return rows.map(row => {
    const mapped = {}
    exportFields.forEach(field => {
      const label = field.label || field.key
      mapped[label] = row[field.key] != null ? row[field.key] : ''
    })
    return mapped
  })
}

export function buildExportWorksheet(rows, sheetName = 'Report', options = {}) {
  const worksheet = XLSX.utils.json_to_sheet(rows)
  if (options.freezeHeader) {
    worksheet['!views'] = [{
      state: 'frozen',
      ySplit: 1,
      topLeftCell: 'A2',
      activeCell: 'A2',
    }]
  }
  return worksheet
}

export function exportRowsToExcel(rows, filename, sheetName = 'Report', options = {}) {
  if (!Array.isArray(rows) || !rows.length) return false

  const worksheet = buildExportWorksheet(rows, sheetName, options)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([buffer], { type: XLSX_MIME })
  saveAs(blob, filename || 'report.xlsx')
  return true
}

export function exportTableToExcel({
  rows,
  fields,
  filename,
  sheetName = 'Report',
  freezeHeader = false,
}) {
  if (!Array.isArray(rows) || !rows.length) return false

  const exportRows = mapTableRowsForExport(rows, fields)
  return exportRowsToExcel(exportRows, filename, sheetName, { freezeHeader })
}
